import { useRef, useState } from 'react';
import { uploadImage, createRow } from '@/lib/api';

interface BulkUploadProps {
  onUploaded: () => void;
}

function titleFromName(name: string): string {
  const base = name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();
  if (!base) return 'Gallery Photo';
  return base
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function BulkUpload({ onUploaded }: BulkUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [category, setCategory] = useState('');
  const [titlePrefix, setTitlePrefix] = useState('');
  const [status, setStatus] = useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const pick = (list: FileList | null) => {
    if (!list) return;
    setFiles(Array.from(list));
    setStatus('idle');
    setMessage('');
  };

  const uploadAll = async () => {
    if (files.length === 0) return;
    setStatus('uploading');
    setMessage('');
    try {
      let done = 0;
      // Upload one at a time so each photo becomes its own gallery entry.
      for (const file of files) {
        const url = await uploadImage(file, 'gallery');
        const name = titleFromName(file.name);
        await createRow('gallery_images', {
          src: url,
          alt: name,
          title: titlePrefix ? `${titlePrefix} ${name}` : name,
          description: '',
          category: category.trim() || 'General',
          sort_order: 0,
          is_published: true,
        });
        done += 1;
      }
      setStatus('done');
      setMessage(`${done} photo${done > 1 ? 's' : ''} added to the gallery.`);
      setFiles([]);
      setTitlePrefix('');
      if (inputRef.current) inputRef.current.value = '';
      onUploaded();
    } catch (err) {
      setStatus('error');
      setMessage(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-background-200 p-6 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <i className="ri-gallery-upload-line text-primary-600" aria-hidden="true" />
        <h3 className="font-heading font-semibold text-base text-foreground-900">Bulk Photo Upload</h3>
      </div>
      <p className="text-sm text-foreground-500 mb-4">
        Select several photos at once — each one is added to the gallery automatically. Give them titles and a
        description afterwards with the Edit button below.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Photos</label>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full btn-outline justify-center whitespace-nowrap text-sm cursor-pointer"
          >
            <i className="ri-add-line" aria-hidden="true" />
            {files.length > 0 ? `${files.length} selected` : 'Choose Photos'}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => pick(e.target.files)}
          />
        </div>
        <div>
          <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Category</label>
          <input
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="e.g. Church Building Project"
            className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900"
          />
        </div>
        <div>
          <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Title Prefix (optional)</label>
          <input
            type="text"
            value={titlePrefix}
            onChange={(e) => setTitlePrefix(e.target.value)}
            placeholder="e.g. New Church"
            className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900"
          />
        </div>
      </div>

      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {files.map((f) => (
            <span key={f.name} className="text-xs bg-background-100 text-foreground-600 px-2 py-1 rounded-md">
              {f.name}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={uploadAll}
          disabled={files.length === 0 || status === 'uploading'}
          className="btn-primary whitespace-nowrap disabled:opacity-50 cursor-pointer"
        >
          {status === 'uploading' ? (
            <>
              <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
              Uploading...
            </>
          ) : (
            <>
              <i className="ri-upload-cloud-2-line" aria-hidden="true" />
              Upload All
            </>
          )}
        </button>
        {message && (
          <p
            className={`text-sm ${
              status === 'error' ? 'text-red-600' : 'text-accent-700'
            }`}
          >
            {message}
          </p>
        )}
      </div>
    </div>
  );
}